import React from "react";

/**
 * Skeleton primitives — shimmer loading placeholders.
 *
 * The `shimmer` keyframe is already injected globally in App.jsx, so we
 * piggyback on it. Each shape (Grid / List / Form / KPI) renders a
 * layout that approximates the loaded view.
 */

const SHIMMER_BG =
  "linear-gradient(90deg, rgba(255,255,255,0.55) 0%, rgba(220,232,250,0.85) 50%, rgba(255,255,255,0.55) 100%)";
const SHIMMER_STYLE = {
  background: SHIMMER_BG,
  backgroundSize: "1000px 100%",
  animation: "shimmer 1.6s linear infinite",
};

export function SkeletonBox({
  width = "100%",
  height = 16,
  radius = 8,
  style = {},
}) {
  return (
    <div
      aria-hidden="true"
      style={{
        width,
        height,
        borderRadius: radius,
        ...SHIMMER_STYLE,
        ...style,
      }}
    />
  );
}

const GLASS = {
  background: "rgba(255,255,255,0.55)",
  backdropFilter: "blur(20px) saturate(160%)",
  WebkitBackdropFilter: "blur(20px) saturate(160%)",
  border: "1px solid rgba(255,255,255,0.45)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6), 0 6px 18px -10px rgba(11,107,211,0.18)",
};

/** Grid of card placeholders (Creative Studio). */
export function SkeletonGrid({ count = 9, columns = 3 }) {
  return (
    <div
      role="status"
      aria-label="Loading"
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(auto-fit, minmax(${Math.max(220, Math.floor(900 / columns))}px, 1fr))`,
        gap: 16,
      }}
    >
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{ ...GLASS, borderRadius: 18, padding: 14, display: "flex", flexDirection: "column", gap: 10 }}
        >
          <SkeletonBox height={140} radius={12} />
          <SkeletonBox width="60%" height={12} />
          <SkeletonBox width="90%" height={10} />
          <SkeletonBox width="40%" height={10} />
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <SkeletonBox width={64} height={26} radius={999} />
            <SkeletonBox width={86} height={26} radius={999} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Vertical list of glass card rows (Schedule). */
export function SkeletonList({ count = 5 }) {
  return (
    <div role="status" aria-label="Loading" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{ ...GLASS, borderRadius: 16, padding: "14px 16px", display: "flex", alignItems: "center", gap: 14 }}
        >
          <SkeletonBox width={36} height={36} radius={12} />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
            <SkeletonBox width="70%" height={12} />
            <SkeletonBox width="40%" height={10} />
          </div>
          <SkeletonBox width={70} height={22} radius={999} />
          <SkeletonBox width={14} height={14} radius={4} />
        </div>
      ))}
    </div>
  );
}

/** Form rows (Settings). */
export function SkeletonForm({ rows = 4 }) {
  return (
    <div role="status" aria-label="Loading" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ ...GLASS, borderRadius: 16, padding: "16px 18px", display: "flex", flexDirection: "column", gap: 10 }}>
          <SkeletonBox width="35%" height={12} />
          <SkeletonBox width="100%" height={36} radius={10} />
          <SkeletonBox width="55%" height={10} />
        </div>
      ))}
    </div>
  );
}

/** KPI stat tiles + chart block (Dashboard). */
export function SkeletonKPI({ tiles = 4 }) {
  return (
    <div role="status" aria-label="Loading" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(auto-fit, minmax(180px, 1fr))`,
          gap: 14,
        }}
      >
        {Array.from({ length: tiles }).map((_, i) => (
          <div
            key={i}
            style={{ ...GLASS, borderRadius: 18, padding: "16px 18px", display: "flex", flexDirection: "column", gap: 10 }}
          >
            <SkeletonBox width="50%" height={10} />
            <SkeletonBox width="70%" height={26} />
            <SkeletonBox width="100%" height={36} radius={6} />
          </div>
        ))}
      </div>
      <div style={{ ...GLASS, borderRadius: 20, padding: 20, display: "flex", flexDirection: "column", gap: 14 }}>
        <SkeletonBox width="30%" height={14} />
        <SkeletonBox width="100%" height={180} radius={12} />
        <div style={{ display: "flex", gap: 8 }}>
          <SkeletonBox width={70} height={22} radius={999} />
          <SkeletonBox width={70} height={22} radius={999} />
          <SkeletonBox width={70} height={22} radius={999} />
        </div>
      </div>
    </div>
  );
}

const _default = { SkeletonBox, SkeletonGrid, SkeletonList, SkeletonForm, SkeletonKPI };
export default _default;
